import PropTypes from "prop-types";
import {
  telegramDisplayMeta,
  whatsappDisplayMeta,
  redditDisplayMeta,
  quoraDisplayMeta,
} from "@/lib/connectionRows";

function GmailConnectionDetail({ row, googleOauthStatus }) {
  if (row?.configured && (row.google_email || row.account_identifier)) {
    return (
      <>
        Connected as <span className="font-medium text-slate-700">{row.google_email || row.account_identifier}</span>
        {row.oauth_validated_at && (
          <span className="block text-[10.5px] text-slate-400 mt-0.5">
            Last verified {new Date(row.oauth_validated_at).toLocaleString()}
          </span>
        )}
      </>
    );
  }
  if (row?.oauth_dev_mock && googleOauthStatus?.mock_mode) {
    return <span className="text-amber-700">Dev mock only — not a real Google account</span>;
  }
  return <span className="text-slate-500">Not connected — use Connect to sign in with Google</span>;
}

GmailConnectionDetail.propTypes = {
  row: PropTypes.object,
  googleOauthStatus: PropTypes.object,
};

function TelegramConnectionDetail({ row, status }) {
  const meta = telegramDisplayMeta(row);
  return (
    <>
      {meta.botUsername && (
        <span className="block">
          Bot <span className="font-medium text-slate-700 font-mono">{meta.botUsername}</span>
          {meta.botName ? ` · ${meta.botName}` : ""}
        </span>
      )}
      {meta.validatedAt && (
        <span className="block text-[10.5px] text-slate-400 mt-0.5">
          Token saved {new Date(meta.validatedAt).toLocaleString()}
        </span>
      )}
      {status === "sync_failed" && (
        <span className="block text-[10.5px] text-amber-700 mt-0.5">
          Bot saved — OpenClaw sync required. Retry Sync, or Re-Configure with a new bot token.
        </span>
      )}
      {meta.ingestMode === "openclaw_transport" && meta.openclawSynced && (
        <span className="block text-[10.5px] text-emerald-700 mt-0.5">
          Routed via OpenClaw transport
        </span>
      )}
      {meta.ingestMode === "polling" && (
        <span className="block text-[10.5px] text-emerald-700 mt-0.5">
          Receiving messages via Sellyx polling
        </span>
      )}
      {meta.ingestMode === "webhook_openclaw" && meta.openclawSynced && (
        <span className="block text-[10.5px] text-emerald-700 mt-0.5">
          Routed via OpenClaw webhook
        </span>
      )}
      {meta.openclawError && (
        <span className="block text-[10.5px] text-rose-700 mt-0.5">
          OpenClaw sync failed: {meta.openclawError}
        </span>
      )}
    </>
  );
}

TelegramConnectionDetail.propTypes = {
  row: PropTypes.object,
  status: PropTypes.string.isRequired,
};

function WhatsappConnectionDetail({ row }) {
  const meta = whatsappDisplayMeta(row);
  return (
    <>
      {meta.phoneNumber && (
        <span className="block">
          Connected as <span className="font-medium text-slate-700">{meta.phoneNumber}</span>
        </span>
      )}
      {meta.displayName && (
        <span className="block text-[10.5px] text-slate-400 mt-0.5">{meta.displayName}</span>
      )}
      {meta.connectedAt && (
        <span className="block text-[10.5px] text-slate-400 mt-0.5">
          Connected {new Date(meta.connectedAt).toLocaleString()}
        </span>
      )}
      <span className="block text-[10.5px] text-emerald-700 mt-0.5 capitalize">
        Status: {meta.connectionStatus}
      </span>
    </>
  );
}

WhatsappConnectionDetail.propTypes = {
  row: PropTypes.object,
};

function LinkedinConnectionDetail({ row }) {
  return (
    <>
      {row.channel_config?.linkedin?.display_name || row.account_identifier || "LinkedIn profile connected"}
      {row.channel_config?.linkedin?.configured_at && (
        <span className="block text-[10.5px] text-slate-400 mt-0.5">
          Connected {new Date(row.channel_config.linkedin.configured_at).toLocaleString()}
        </span>
      )}
    </>
  );
}

LinkedinConnectionDetail.propTypes = {
  row: PropTypes.object,
};

function RedditConnectionDetail({ row }) {
  const meta = redditDisplayMeta(row);
  return (
    <>
      {meta.username && (
        <span className="block">
          Connected as <span className="font-medium text-slate-700">{meta.username}</span>
        </span>
      )}
      {meta.validatedAt && (
        <span className="block text-[10.5px] text-slate-400 mt-0.5">
          Last verified {new Date(meta.validatedAt).toLocaleString()}
        </span>
      )}
    </>
  );
}

RedditConnectionDetail.propTypes = {
  row: PropTypes.object,
};

function QuoraConnectionDetail({ row }) {
  const meta = quoraDisplayMeta(row);
  return (
    <>
      {meta.displayName && (
        <span className="block font-medium text-slate-700">{meta.displayName}</span>
      )}
      {meta.profileUrl && (
        <span className="block text-[10.5px] text-slate-400 mt-0.5 truncate">{meta.profileUrl}</span>
      )}
      {meta.openclawSynced === false && (
        <span className="block text-[10.5px] text-amber-700 mt-0.5">
          Profile saved — OpenClaw browser session pending
        </span>
      )}
    </>
  );
}

QuoraConnectionDetail.propTypes = {
  row: PropTypes.object,
};

export function ConnectionChannelDetail({ c, row, status, googleOauthStatus }) {
  if (status === "coming_soon" && (c.kind === "reddit" || c.kind === "quora")) {
    return "This channel will be available soon.";
  }
  if (c.kind === "gmail") {
    return <GmailConnectionDetail row={row} googleOauthStatus={googleOauthStatus} />;
  }
  if (c.kind === "telegram" && (status === "connected" || status === "sync_failed")) {
    return <TelegramConnectionDetail row={row} status={status} />;
  }
  if (c.kind === "whatsapp" && status === "connected") {
    return <WhatsappConnectionDetail row={row} />;
  }
  if (c.kind === "linkedin" && status === "connected") {
    return <LinkedinConnectionDetail row={row} />;
  }
  if (c.kind === "reddit" && status === "connected") {
    return <RedditConnectionDetail row={row} />;
  }
  if (c.kind === "quora" && status === "connected") {
    return <QuoraConnectionDetail row={row} />;
  }
  return c.tooltip;
}

ConnectionChannelDetail.propTypes = {
  c: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    tooltip: PropTypes.node,
  }).isRequired,
  row: PropTypes.object,
  status: PropTypes.string.isRequired,
  googleOauthStatus: PropTypes.object,
};
